/**
 * useUserNotifications
 *
 * Hook for the bell icon inbox: list, unread badge, mark-read, mark-all-read.
 * Polls every 60s and refreshes on window focus so the badge stays fresh
 * without a websocket dependency.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getUnreadCount,
  listUserNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/user-notifications.service';
import type { UserNotification } from '../types/user-notifications.types';

const POLL_INTERVAL_MS = 60_000;
const LIST_LIMIT = 30;

interface UseUserNotificationsResult {
  items: UserNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useUserNotifications = (): UseUserNotificationsResult => {
  const [items, setItems] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const [list, count] = await Promise.all([
        listUserNotifications({ limit: LIST_LIMIT }),
        getUnreadCount(),
      ]);
      if (!mountedRef.current) return;
      setItems(list.items);
      setUnreadCount(count);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();

    const intervalId = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    const onFocus = (): void => void refresh();
    window.addEventListener('focus', onFocus);

    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const markRead = useCallback(
    async (id: string) => {
      // Optimistic update — flip readAt locally so the badge drops immediately.
      setItems((prev) =>
        prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await markNotificationRead(id);
      } catch {
        void refresh();
      }
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      void refresh();
    }
  }, [refresh]);

  return { items, unreadCount, isLoading, error, refresh, markRead, markAllRead };
};
